import { useCallback, useEffect, useRef, useState } from "react";
import { InferenceTypes } from "@functions/rapid/services/inference/stream/types";
import { useModel } from "./contexts/ModelContext";
import { useAnalytics } from "./contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useFlow } from "./contexts/FlowContext";
import { useFlowState } from "@/hooks/useFlowState";
import { Flows, DeploySteps } from "@/types/flows";
import { useGetPredictions } from "./hooks/useGetPredictions";
import { useMedia } from "./contexts/MediaContext";
import { useCurrentWorkflow, useCurrentWorkspaceApiKey } from "@/store";
import { MediaItem, MediaItemUuid, MediaItemFile } from "@/types/media";
import { UploadLimitErrorType } from "@/constants/media";
import { validateUploadLimits as validateUploadLimitsUtil } from "@/utils/mediaValidation";
import { useRunInference } from "./hooks/useRunInference";

export const useTestWithMoreFiles = () => {
    const { track } = useAnalytics();
    const { task } = useFlow();
    const { flow, step } = useFlowState();
    const {
        addMediaItems,
        completedMediaItems,
        uploadLimitError,
        setUploadLimitError,
        clearUploadLimitError
    } = useMedia();
    const { setIsTestingNewFiles, setTestingProgress, annotationsForFileAndFrameId } = useModel();
    const { getPredictionsForFile } = useGetPredictions();
    const { runInference } = useRunInference();
    const [testFileMediaItemIds, setTestFileMediaItemIds] = useState<MediaItemUuid[]>([]);
    const [isOutOfCredits, setIsOutOfCredits] = useState(false);
    const testFilesRef = useRef<MediaItemUuid[]>([]);
    const isTestingRef = useRef(false);
    const workflow = useCurrentWorkflow();
    const apiKey = useCurrentWorkspaceApiKey();

    const uploadAndProcessFiles = useCallback(
        async (files: MediaItemFile[]) => {
            const createdMediaItems = await addMediaItems(files);
            const mediaItemIds = createdMediaItems.map((i) => i.id);

            setTestFileMediaItemIds(mediaItemIds);
            setTestingProgress(25);

            // Return the created media item IDs so we can track them
            return createdMediaItems;
        },
        [addMediaItems, setTestingProgress]
    );

    const validateUploadLimits = useCallback(
        (files: MediaItemFile[]): UploadLimitErrorType | null => {
            // Skip validation for improve flow (allows more images)
            if (step === DeploySteps.IMPROVE) {
                return null;
            }

            // Only apply limits in deploy, share, and build flows
            if (flow !== Flows.DEPLOY && flow !== Flows.SHARE && flow !== Flows.BUILD) {
                return null;
            }

            return validateUploadLimitsUtil(files);
        },
        [flow, step]
    );

    /**
     * Runs the appropriate inference type based on the current flow.
     */
    const runInferenceForFlow = useCallback(
        async (mediaItem: MediaItem) => {
            const abortController = new AbortController();

            switch (flow) {
                case Flows.DEPLOY:
                    if (workflow?.id && apiKey) {
                        await getPredictionsForFile({
                            mediaItem,
                            abortController,
                            useInferenceQueue: true,
                            inferenceType: InferenceTypes.WORKFLOW
                        });
                    }
                    break;

                case Flows.SHARE:
                    await getPredictionsForFile({
                        mediaItem,
                        abortController,
                        useInferenceQueue: true,
                        inferenceType: InferenceTypes.WORKFLOW_PUBLIC
                    });
                    break;

                case Flows.BUILD:
                    // Use centralized inference hook for BUILD flow
                    // This automatically handles mixed inference (SAM3 + OWLv2)
                    // with dynamic confidence thresholds for OWLv2
                    await runInference({ mediaItem, annotationsForFileAndFrameId, isDraft: false });
                    break;

                default:
                    // Fallback to SAM3 inference
                    await getPredictionsForFile({
                        mediaItem,
                        abortController,
                        useInferenceQueue: true
                    });
            }
        },
        [flow, workflow, apiKey, getPredictionsForFile, runInference, annotationsForFileAndFrameId]
    );

    /**
     * Shared inference runner: runs inference for a set of media items.
     * Used both directly (for skipUpload files) and via the effect (for normal uploads).
     */
    const runInferenceForMediaItems = useCallback(
        async (mediaItems: MediaItem[]) => {
            if (mediaItems.length === 0 || isTestingRef.current) return;

            isTestingRef.current = true;

            try {
                const totalFiles = mediaItems.length;
                let completedFiles = 0;

                const updateProgress = () => {
                    const uploadProgress = 50; // Uploads are done
                    const predictionProgress = (completedFiles / totalFiles) * 50;
                    setTestingProgress(Math.round(uploadProgress + predictionProgress));
                };

                // Process predictions for each test file
                const predictionPromises = mediaItems.map(async (mediaItem) => {
                    try {
                        // Track analytics
                        track({
                            event: AnalyticsFlowEvent.MODEL_TESTED,
                            data: {
                                task,
                                file_uuid: mediaItem.id,
                                step,
                                flow,
                                type: mediaItem.isVideo ? "video" : "image",
                                name: mediaItem.file.name,
                                size: mediaItem.file.size,
                                size_mb: mediaItem.file.size / 1024 / 1024,
                                format: mediaItem.file.type,
                                metadata: mediaItem.metadata?.serverMediaDimensions
                            }
                        });

                        // Small delay to ensure React state is properly synchronized
                        await new Promise((resolve) => setTimeout(resolve, 1));

                        // Run inference based on flow type
                        await runInferenceForFlow(mediaItem);

                        completedFiles++;
                        updateProgress();
                    } catch (error: any) {
                        console.error(`Failed to process ${mediaItem.file.name}:`, error);

                        if (error?.statusCode === 402 || error?.message?.includes("402")) {
                            setIsOutOfCredits(true);
                            throw error;
                        }

                        completedFiles++;
                        updateProgress();
                    }
                });

                await Promise.allSettled(predictionPromises);
                setTestingProgress(100);
            } catch (error) {
                console.error("Error triggering upload queue tests:", error);
            } finally {
                setIsTestingNewFiles(false);
                setTestingProgress(0);
                isTestingRef.current = false;
                setTestFileMediaItemIds([]);
                testFilesRef.current = [];
            }
        },
        [task, track, setIsTestingNewFiles, setTestingProgress, step, flow, runInferenceForFlow]
    );

    const testWithMoreFiles = useCallback(
        async (files: MediaItemFile[], onFinally?: () => void) => {
            if (!files || files.length === 0 || testFilesRef.current.length !== 0) {
                return;
            }

            // Validate upload limits
            const limitError = validateUploadLimits(files);
            if (limitError) {
                setUploadLimitError(limitError);
                onFinally?.(); // Close modal - FloatingHint will show the error
                return;
            }

            setUploadLimitError(null);
            onFinally?.();

            setIsTestingNewFiles(true);
            setTestingProgress(0);
            setTestFileMediaItemIds([]);

            try {
                const createdMediaItems = await uploadAndProcessFiles(files);
                testFilesRef.current = createdMediaItems.map((i) => i.id);

                // For skipUpload files (e.g., random source images), run inference directly.
                // These files are already COMPLETED so the effect-based path may miss them
                // due to a race condition with setTestFileMediaItemIds.
                const allSkipUpload = files.every((f) => (f as MediaItemFile).skipUpload);
                if (allSkipUpload) {
                    // Fire inference in background - don't await so the image shows immediately
                    // while inference runs. The caller (e.g. useRandomSourceLoader) can release
                    // the loading state as soon as media items are created.
                    runInferenceForMediaItems(createdMediaItems);
                }

                return createdMediaItems;
            } catch (error) {
                console.error("Error uploading files:", error);
                setIsTestingNewFiles(false);
                setTestingProgress(0);
                setTestFileMediaItemIds([]);
            }
        },
        [
            setIsTestingNewFiles,
            setTestingProgress,
            uploadAndProcessFiles,
            validateUploadLimits,
            setUploadLimitError,
            runInferenceForMediaItems
        ]
    );

    const getPredictionsForTestFiles = useCallback(async () => {
        if (testFileMediaItemIds.length === 0) {
            return;
        }

        // Find the current test media items from completed upload media items
        const currentTestFileMediaItems = testFileMediaItemIds
            .map((id) => completedMediaItems.find((u) => u.id === id))
            .filter(Boolean) as MediaItem[];

        // Check if all test files have completed uploading
        const allFilesReady =
            currentTestFileMediaItems.length === testFileMediaItemIds.length &&
            currentTestFileMediaItems.length > 0;

        if (!allFilesReady) {
            return;
        }

        await runInferenceForMediaItems(currentTestFileMediaItems);
    }, [testFileMediaItemIds, completedMediaItems, runInferenceForMediaItems]);

    useEffect(() => {
        getPredictionsForTestFiles();
    }, [getPredictionsForTestFiles]);

    return {
        testWithMoreFiles,
        uploadLimitError,
        clearUploadLimitError,
        isOutOfCredits,
        clearOutOfCreditsError: () => setIsOutOfCredits(false)
    };
};
